import { cn } from "@web/src/lib/utils";
import type { ComponentPropsWithoutRef, CSSProperties, FC } from "react";

type NativeInputProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "type" | "value" | "min" | "max" | "step" | "onChange"
>;

interface RangeSliderProps extends NativeInputProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  onValueChange: (value: number) => void;
}

/**
 * Single-handle slider.
 *
 * A native range input under the hood, but the track is ours: the `.range-slider`
 * rule in globals.css paints the unfilled half in surface-tertiary so these match
 * the hand-rolled dual-handle sliders in both themes, and the filled half comes
 * from the `--range-fill` percentage set here. Override the colour with
 * `[--range-accent:var(--color-theme-blue)]`, which moves the thumb too.
 */
export const RangeSlider: FC<RangeSliderProps> = ({
  value,
  min,
  max,
  step,
  onValueChange,
  className,
  style,
  ...rest
}) => {
  const span = max - min;
  const fill = span > 0 ? ((value - min) / span) * 100 : 0;

  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onValueChange(Number(event.target.value))}
      style={
        {
          ...style,
          "--range-fill": `${Math.max(0, Math.min(100, fill))}%`,
        } as CSSProperties
      }
      className={cn("range-slider w-full h-1.5", className)}
      {...rest}
    />
  );
};
